import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  guardianReportIssueSchema,
  INITIAL_GUARDIAN_REPORT_ISSUE_FORM,
  type GuardianReportIssueForm,
} from '@/features/guardian-profile/schemas/guardian-report-issue-schema';
import { useSessionStore } from '@/stores/session-store';

import { useGuardianProfile } from './use-guardian-profile';

/**
 * react-hook-form instance for the "Report an issue" sheet (validated on submit).
 * Name and email come from the signed-in guardian's account and are shown read-only (BR-01).
 *
 * Pass the sheet's `open` flag so each time it opens the form re-seeds: the prefill is restored
 * and Issue / Description start empty again (Scenario 1, and after a dismiss in Scenario 4).
 */
export function useGuardianReportIssueForm(open: boolean) {
  const { data: profile } = useGuardianProfile();
  const sessionEmail = useSessionStore(state => state.user?.email) ?? '';

  const defaultValues = useMemo<GuardianReportIssueForm>(
    () => ({
      ...INITIAL_GUARDIAN_REPORT_ISSUE_FORM,
      firstName: profile?.firstname ?? '',
      lastName: profile?.lastname ?? '',
      email: profile?.email ?? sessionEmail,
    }),
    [profile?.firstname, profile?.lastname, profile?.email, sessionEmail],
  );

  const form = useForm<GuardianReportIssueForm>({
    mode: 'onSubmit',
    resolver: yupResolver(guardianReportIssueSchema) as never,
    defaultValues,
  });

  const { reset } = form;
  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  return form;
}

export default useGuardianReportIssueForm;
